import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { formatMoney, parseMoneyToCents, pickLocale } from '../lib/format';

type I18nText = Record<string, string>;
interface MenuItem { id: string; name: I18nText; price_cents: number }
interface Line { key: string; label: string; menu_item_id?: string; custom_name?: string; unit_price_cents?: number; qty: number }

interface Props {
  restaurantId: string;
  tabId: string;
  currency: string;
  locale: string;
  onDone: () => void;
}

export function AddItemsPanel({ restaurantId, tabId, currency, locale, onDone }: Props) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [sel, setSel] = useState('');
  const [customName, setCustomName] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void supabase
      .from('menu_items')
      .select('id, name, price_cents')
      .eq('restaurant_id', restaurantId)
      .eq('is_available', true)
      .order('position')
      .then(({ data }) => { if (data) setMenu(data as MenuItem[]); });
  }, [restaurantId]);

  function addMenu() {
    const m = menu.find((x) => x.id === sel);
    if (!m) return;
    setLines((p) => [...p, { key: crypto.randomUUID(), label: pickLocale(m.name, locale), menu_item_id: m.id, qty: 1 }]);
    setSel('');
  }
  function addCustom() {
    if (!customName.trim() || !customValue) return;
    setLines((p) => [...p, { key: crypto.randomUUID(), label: customName.trim(), custom_name: customName.trim(), unit_price_cents: parseMoneyToCents(customValue), qty: 1 }]);
    setCustomName('');
    setCustomValue('');
  }
  function setQty(key: string, qty: number) {
    setLines((p) => (qty <= 0 ? p.filter((l) => l.key !== key) : p.map((l) => (l.key === key ? { ...l, qty } : l))));
  }

  async function launch() {
    if (lines.length === 0) return;
    setBusy(true);
    const items = lines.map((l) => (l.menu_item_id
      ? { menu_item_id: l.menu_item_id, quantity: l.qty }
      : { custom_name: l.custom_name, unit_price_cents: l.unit_price_cents, quantity: l.qty }));
    const { error } = await supabase.rpc('staff_place_order', { p_tab_id: tabId, p_items: items });
    setBusy(false);
    if (!error) { setLines([]); onDone(); }
  }

  return (
    <div className="add-panel">
      <div className="add-panel__row">
        <select value={sel} onChange={(e) => setSel(e.target.value)}>
          <option value="">{t('sessions.pick_menu_item')}</option>
          {menu.map((m) => (
            <option key={m.id} value={m.id}>{pickLocale(m.name, locale)} · {formatMoney(m.price_cents, currency, locale)}</option>
          ))}
        </select>
        <button type="button" className="btn btn--ghost btn--sm" disabled={!sel} onClick={addMenu}>{t('sessions.from_menu')}</button>
      </div>

      <div className="add-panel__row">
        <input placeholder={t('sessions.custom_name')} value={customName} onChange={(e) => setCustomName(e.target.value)} style={{ flex: 2 }} />
        <input placeholder={t('sessions.custom_value')} inputMode="decimal" value={customValue} onChange={(e) => setCustomValue(e.target.value)} style={{ width: 90 }} />
        <button type="button" className="btn btn--ghost btn--sm" disabled={!customName.trim() || !customValue} onClick={addCustom}>{t('sessions.custom_item')}</button>
      </div>

      {lines.length > 0 && (
        <ul className="add-panel__lines">
          {lines.map((l) => (
            <li key={l.key}>
              <span>{l.label}{l.unit_price_cents != null && <span className="dimmed"> · {formatMoney(l.unit_price_cents, currency, locale)}</span>}</span>
              <span className="cart-line__controls">
                <button type="button" onClick={() => setQty(l.key, l.qty - 1)}>−</button>
                <span>{l.qty}</span>
                <button type="button" onClick={() => setQty(l.key, l.qty + 1)}>+</button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="btn btn--primary btn--sm btn--block" disabled={busy || lines.length === 0} onClick={() => void launch()}>
        {busy ? t('sessions.launching') : t('sessions.launch')}
      </button>
    </div>
  );
}
